<template>
  <div>
    <b-container class="wide">
      <div class="d-flex" v-if="selectedProductSheet">
        <head-block :title="$t('title.customer.product_sheet_detail', {productSheetName: selectedProductSheet.name})" />
        <b-button
          size="sm"
          style="max-height: 30px;"
          class="ml-2 align-self-center"
          v-b-tooltip.hover
          :title="$t('table.product_sheets.button_edit')"
          variant="primary"
          @click="editProductSheet()">
          <b-icon icon="pencil-square"></b-icon>
        </b-button>
      </div>
      <div class="mb-3 mt-3">
        <b-button
          variant="outline-primary"
          :to="{ name: 'product-sheets' }"
          v-html="$t('links.user.go_back')"
        >
        </b-button>
      </div>
      <div class="d-flex" v-if="selectedProductSheet && editableStatuses.includes(selectedProductSheet.status)">
        <div>
          <b-button
            class="mb-2 mt-2 mr-3"
            variant="success"
            @click="checkProductsValidity">
            {{ $t('table.products.button_save') }}
          </b-button>
        </div>

        <div>
          <b-button
            v-if="$refs.bulkCreateProductModal && $refs.bulkCreateProductModal.maxAmount > 0"
            class="mb-2 mt-2"
            variant="primary"
            @click="$bvModal.show('bulk-create-product')">
            {{ $t('table.products.button_bulk_create') }}
          </b-button>
        </div>

      </div>
      <content-table
        :fields="fields"
        :items="productItems"
        :slots="templates"
        :primary-key="primaryKey"
        head-variant="dark"
      >
        <template slot="name" slot-scope="data">
          <b-form v-if="selectedProductSheet && editableStatuses.includes(selectedProductSheet.status)">
            <b-form-group
              :invalid-feedback="invalidName(data.tbl.item)"
              :state="stateName(data.tbl.item)"
            >
              <b-form-input
                @keyup.enter="$event.target.blur()"
                @focus="focusInput($event, data.tbl.item, ['name'])"
                @blur="verifyNameForm($event, data.tbl.item)"
                @input="setName($event, data.tbl.item)"
                :state="stateNameInput(data.tbl.item)"
                :value="data.tbl.item.name"
              />
            </b-form-group>
          </b-form>
          <span v-else>{{ data.tbl.item.name }}</span>
        </template>
        <template slot="price" slot-scope="data">
          <b-form v-if="selectedProductSheet && editableStatuses.includes(selectedProductSheet.status)">
            <b-form-group
              :invalid-feedback="invalidPrice(data.tbl.item)"
              :state="statePrice(data.tbl.item)"
            >
              <b-form-input
                @keyup.enter="$event.target.blur()"
                @focus="focusInput($event, data.tbl.item, ['sellPrice'])"
                @blur="verifyPriceForm($event, data.tbl.item)"
                @input="setPrice($event, data.tbl.item)"
                :state="statePriceInput(data.tbl.item)"
                :value="data.tbl.item.sellPrice"
              />
            </b-form-group>
          </b-form>
          <span v-else>{{ data.tbl.item.sellPrice }}</span>
        </template>
        <template slot="actions" slot-scope="data">
          <b-button
            v-if="soldStatuses.includes(data.tbl.item.status)"
            size="sm"
            v-b-tooltip.hover
            :title="$t('table.products.button_view_product_sales')"
            variant="info"
            :to="{name: 'product-sheet-product-sale-lines', params: { productSheetId: selectedProductSheet.id, productId: data.tbl.item.id }}">
            <b-icon icon="view-stacked"></b-icon>
          </b-button>
        </template>
        <template slot="status" slot-scope="data">
          <div class="d-flex justify-content-center">
            <span v-if="selectedProductSheet && editableStatuses.includes(selectedProductSheet.status)">
              {{ getObjectByKeyValue(productStatuses, 'code', data.tbl.item.status).name }}
              {{ data.tbl.item.status === statusDraft && (data.tbl.item.name && data.tbl.item.sellPrice) ? ` - ${$t('table.products.status_product_info_filled_in')}` : '' }}
              {{ (!data.tbl.item.name || !data.tbl.item.sellPrice) ? ` - ${$t('table.products.status_product_info_not_filled_in')}` : '' }}
            </span>
            <span v-else>{{ getObjectByKeyValue(productStatuses, 'code', data.tbl.item.status).name }}</span>
          </div>
        </template>
      </content-table>
      <bulk-create-product-modal
        modalId="bulk-create-product"
        :productsList="productItems"
        ref="bulkCreateProductModal"
        @productsGenerated="updateProductItems"
      />
      <confirm-item-modal
        modalId="save-products"
        :okButtonTitle="$t('modal.general.ok_button')"
        :cancelButtonTitle="$t('modal.general.cancel_button')"
        :title="$t('modal.products.title_save')"
        :text="$t('modal.products.text_save')"
        @onConfirm="saveProducts"/>
      <edit-product-sheet-modal
        modalId="edit-product-sheet"
        v-if="productSheetObj"
        :selectedObject="productSheetObj"/>
    </b-container>
  </div>
</template>

<script>

  import { mapState } from 'vuex'
  import ContentTable from '@base/components/ContentTable'
  import Formatting from '@base/mixins/Formatting.vue'
  import BulkCreateProductModal from '@user/components/BulkCreateProductModal.vue'
  import EditProductSheetModal from '@user/components/EditProductSheetModal.vue'
  import HeadBlock from '@base/components/HeadBlock.vue'
  import { decimalValidator, noQuotesValidator } from '@base/customValidators'
  import ConfirmItemModal from '@base/components/ConfirmItemModal.vue'
  import ObjectUtils from '@base/mixins/ObjectUtils.vue'
  import { EDITABLE_STATUSES, SOLD_STATUSES, PRODUCT_DRAFT, PRODUCT_NAME_MAX_LENGTH, PRODUCT_STATUSES } from '@user/configs'
  import { maxLength } from 'vuelidate/lib/validators'
  import { USED_PREFIX } from "@base/configs";

  export default {
    name: 'CustomerProductSheetDetailView',

    components: {
      EditProductSheetModal,
      ConfirmItemModal,
      HeadBlock,
      BulkCreateProductModal,
      ContentTable,
    },

    mixins: [ Formatting, ObjectUtils ],

    data() {
      return {
        primaryKey: 'number',
        sortBy: 'number',
        sortDesc: false,
        templates: [
          {name: 'name', field: 'name'},
          {name: 'price', field: 'sellPrice'},
          {name: 'status', field: 'status'},
          { name: 'actions', field: 'actions' },
        ],
        selectedObject: null,
        productSheetObj: null,
        inputFocused: false,
        loading: false,
        productItems: [],
        productStatuses: PRODUCT_STATUSES,
        editableStatuses: EDITABLE_STATUSES,
        soldStatuses: SOLD_STATUSES,
        statusDraft: PRODUCT_DRAFT,
      }
    },

    validations: {
      selectedObject: {
        name: {
          maxLength: maxLength(PRODUCT_NAME_MAX_LENGTH),
          noQuotesValidator,
        },
        sellPrice: {
          decimal: decimalValidator
        }
      }
    },

    created() {
      this.getProductSheet()
    },

    computed: {
      ...mapState(['selectedProductSheet', 'userMarket']),

      genericProductName () {
        if (this.userMarket && this.userMarket.supportCountry && this.selectedProductSheet) {
          if (this.selectedProductSheet.productType === USED_PREFIX) {
            return this.availableCountriesList.find(
                obj => obj.code === this.userMarket.supportCountry.code
            ).genericProductNameUsed
          } else {
            return this.availableCountriesList.find(
                obj => obj.code === this.userMarket.supportCountry.code
            ).genericProductNameNew
          }
        }
        return ''
      },

      fields () {
        const fieldsArray = [
          {key: 'number', label: this.$t('table.products.number'), class: 'text-center number-thead-width'},
          {key: 'name', label: this.$t('table.products.name'), class: 'text-center name-thead-width'},
          {key: 'sellPrice', label: this.$t('table.products.sell_price'), class: 'text-center price-thead-width'},
          {key: 'status', label: this.$t('table.products.status'), class: 'text-center'},
        ]
        // Show discount column only for active products
        if (this.selectedProductSheet && !this.editableStatuses.includes(this.selectedProductSheet.status)) {
          fieldsArray.splice(
            4,
            0,
            { key: 'actions', label: this.$t('form.general.actions'), class: 'text-center' },
          )
        }
        return fieldsArray
      }
    },

    methods: {

      getProductSheet() {
        return new Promise((resolve) => {
          if (this.loading)
            return
          this.loading = true
          this.loader = this.$loading.show(this.$_loaderOptions)
          this.$store.commit(this.$_mutationTypes.SET_PRODUCT_SHEET, null)
          this.$store.dispatch(this.$_actionTypes.GET_USER_PRODUCT_SHEET, this.$route.params.productSheetId)
            .then(() => {
              this.setInitialProducts()
            })
            .catch((error) => {
              this.$_notifyError(error, this)
            })
            .finally(() => {
              this.loader.hide()
              this.loading = false
              resolve()
            })
        })
      },

      checkUpdateProductList () {
        this.$v.selectedObject.$touch()
        if (this.selectedObject.name && this.selectedObject.sellPrice && !this.$v.selectedObject.$error) {
          this.updateProductList()
        } else if (!this.selectedObject.name && !this.selectedObject.sellPrice) {
          this.$refs.bulkCreateProductModal.updateMaxAmount(this.productItems)
        }
      },

      focusInput (e, item, fields) {
        for (const field of fields)
          this.$v.selectedObject[field].$touch()
        this.inputFocused = true
        this.selectedObject = this.$_.cloneDeep(item)
      },

      verifyNameForm (e, selectedObject) {
        this.inputFocused = false
        this.selectedObject = selectedObject
        e.preventDefault()
        this.$v.selectedObject.name.$touch()
        if (this.$v.selectedObject.name.$error) {
          this.selectedObject['_cellVariants'] = Object.assign({ name: 'danger'}, this.selectedObject['_cellVariants'])
          return
        } else if (this.selectedObject['_cellVariants']['name']) {
          delete this.selectedObject['_cellVariants']['name']
        }
        this.checkUpdateProductList()
      },

      setName (value, item) {
        item.name = value
        this.selectedObject = this.$_.cloneDeep(item)
        this.$v.selectedObject.name.$touch()
      },

      stateNameInput (obj) {
        return (this.$v.selectedObject.$model &&
          obj.number === this.$v.selectedObject.$model.number &&
          this.$v.selectedObject.name.$error) ? false : null
      },
      stateName (obj) {
        return (this.$v.selectedObject.$model &&
          obj.number === this.$v.selectedObject.$model.number &&
          !this.$v.selectedObject.name.$error)
      },
      invalidName (obj) {
        if (this.$v.selectedObject.$model &&
          obj.number === this.$v.selectedObject.$model.number &&
          this.$v.selectedObject.name.$error) {
          if (!this.$v.selectedObject.name.maxLength)
            return this.$t('validation.max_length', {
              field: this.$t('form.products.name_field'),
              maxLength: this.$v.selectedObject.name.$params.maxLength.max
            })
          else if (!this.$v.selectedObject.name.noQuotesValidator)
            return this.$t('validation.quotes_not_allowed', { field: this.$t('form.products.name_field') })
        }
        return null
      },

      verifyPriceForm (e, selectedObject) {
        this.inputFocused = false
        this.selectedObject = selectedObject
        e.preventDefault()
        this.$v.selectedObject.sellPrice.$touch()
        if (this.$v.selectedObject.sellPrice.$error) {
          this.selectedObject['_cellVariants'] = Object.assign({ sellPrice: 'danger'}, this.selectedObject['_cellVariants'])
          return
        } else if (this.selectedObject['_cellVariants']['sellPrice']) {
          delete this.selectedObject['_cellVariants']['sellPrice']
        }
        this.checkUpdateProductList()
      },

      updateProductList () {
        const oldProductObj = this.productItems.find(obj => obj.number === this.selectedObject.number)
        Object.assign(oldProductObj, this.selectedObject)
        this.$refs.bulkCreateProductModal.updateMaxAmount(this.productItems)
      },

      setPrice (value, item) {
        item.sellPrice = value
        this.selectedObject = this.$_.cloneDeep(item)
        this.$v.selectedObject.sellPrice.$touch()
      },

      statePriceInput (obj) {
        return (this.$v.selectedObject.$model &&
          obj.number === this.$v.selectedObject.$model.number &&
          this.$v.selectedObject.sellPrice.$error) ? false : null
      },
      statePrice (obj) {
        return (this.$v.selectedObject.$model &&
          obj.number === this.$v.selectedObject.$model.number &&
          !this.$v.selectedObject.sellPrice.$error)
      },
      invalidPrice (obj) {
        if (this.$v.selectedObject.$model &&
          obj.number === this.$v.selectedObject.$model.number &&
          this.$v.selectedObject.sellPrice.$error) {
          if (!this.$v.selectedObject.sellPrice.decimal)
            return this.$t('validation.decimal', {field: this.$t('form.products.sell_price_field')})
        }
        return null
      },

      editProductSheet () {
        this.productSheetObj = this.$_.cloneDeep(this.selectedProductSheet)
        this.$nextTick(() => {
          this.$bvModal.show('edit-product-sheet')
        })
      },

      setInitialProducts () {
        // Reset products list
        this.productItems = []
        this.productItems['_cellVariants'] = {}

        for (let number = this.selectedProductSheet.productIdFrom; number <= this.selectedProductSheet.productIdTo; number++) {
          const newProductObj = {
            productSheet: this.$route.params.productSheetId,
            id: null,
            number: number,
            name: null,
            sellPrice: null,
            status: this.statusDraft,
            _cellVariants: {},
          }
          if (this.selectedProductSheet.products.length) {
            const existingProductObj = this.selectedProductSheet.products.find(obj => obj.sheetProductId === number)
            if (existingProductObj) {
              newProductObj.id = existingProductObj.id
              newProductObj.name = existingProductObj.name
              newProductObj.sellPrice = existingProductObj.sellPrice
              newProductObj.status = existingProductObj.status
            }
          }
          this.productItems.push(newProductObj)
        }
        this.$refs.bulkCreateProductModal.updateMaxAmount(this.productItems)
      },

      updateProductItems (productList) {
        this.productItems = this.$_.cloneDeep(productList)
      },

      checkProductsValidity () {
        if (this.productItems.filter(product => !product.name || !product.sellPrice).length) {
          this.$bvModal.show('save-products')
        } else {
          this.saveProducts()
        }
      },

      saveProducts () {
        if (this.loading)
          return
        this.$v.$touch()
        for (const [index, product] of this.productItems.entries()) {
          if (this.$v.$invalid || Object.keys(product['_cellVariants']).length) {
            this.$notify({
              group: 'app',
              type: 'warn',
              title: this.$t('notifications.title.warning'),
              text: this.$t('notifications.text.products.warning_correct_errors')
            })
            return
          }
          this.productItems[index].name = this.productItems[index].name ? this.productItems[index].name : null
          this.productItems[index].sellPrice = this.productItems[index].sellPrice ? this.productItems[index].sellPrice : null
        }
        this.loading = true
        this.loader = this.$loading.show(this.$_loaderOptions)
        this.$store.dispatch(this.$_actionTypes.CREATE_USER_PRODUCTS, this.productItems)
          .then(() => {
            this.$nextTick(() => {
              this.$router.push({name: 'product-sheets'})
            })
            this.$notify({
              group: 'app',
              type: 'success',
              title: this.$t('notifications.title.success'),
              text: this.$t('notifications.text.products.success_products_created')
            })
          })
          .catch((error) => {
            this.$_notifyError(error.response, this)
          })
          .finally(() => {
            this.loader.hide()
            this.loading = false
          })
      }
    }
  }

</script>

<style>

  .number-thead-width {
    min-width: 50px;
    max-width: 50px;
  }
  .name-thead-width {
    max-width: 250px;
  }
  .price-thead-width {
    max-width: 200px;
  }
  .discount-thead-width {
    max-width: 100px;
  }

</style>
